import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { Radio, At, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const forgotPasswordTexts = {
    de: {
        title: "Passwort zurücksetzen",
        subtitle:
            "Gib deine registrierte E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen.",
        emailAddress: "E-Mail-Adresse",
        emailPlaceholder: "you@example.com",
        sendResetLink: "Reset-Link senden",
        sending: "Wird gesendet...",
        backToLogin: "Zurück zum Login",
        backToHome: "Zurück zur Startseite",
        missingEmail: "Bitte gib deine E-Mail-Adresse ein.",
        success:
            "Wenn ein Konto mit dieser E-Mail existiert, haben wir dir einen Link zum Zurücksetzen gesendet.",
        failed:
            "Der Reset-Link konnte nicht gesendet werden. Bitte versuche es erneut.",
    },

    en: {
        title: "Reset password",
        subtitle:
            "Enter your registered email address. We will send you a reset link.",
        emailAddress: "Email address",
        emailPlaceholder: "you@example.com",
        sendResetLink: "Send reset link",
        sending: "Sending...",
        backToLogin: "Back to login",
        backToHome: "Back to home",
        missingEmail: "Please enter your email address.",
        success:
            "If an account exists for this email, we sent you a password reset link.",
        failed:
            "The reset link could not be sent. Please try again.",
    },

    no: {
        title: "Tilbakestill passord",
        subtitle:
            "Skriv inn den registrerte e-postadressen din. Vi sender deg en lenke for tilbakestilling.",
        emailAddress: "E-postadresse",
        emailPlaceholder: "you@example.com",
        sendResetLink: "Send tilbakestillingslenke",
        sending: "Sender...",
        backToLogin: "Tilbake til innlogging",
        backToHome: "Tilbake til startsiden",
        missingEmail: "Skriv inn e-postadressen din.",
        success:
            "Hvis det finnes en konto med denne e-postadressen, har vi sendt deg en lenke for å tilbakestille passordet.",
        failed:
            "Tilbakestillingslenken kunne ikke sendes. Prøv igjen.",
    },

    ko: {
        title: "비밀번호 재설정",
        subtitle:
            "등록된 이메일 주소를 입력하세요. 비밀번호 재설정 링크를 보내드립니다.",
        emailAddress: "이메일 주소",
        emailPlaceholder: "you@example.com",
        sendResetLink: "재설정 링크 보내기",
        sending: "보내는 중...",
        backToLogin: "로그인으로 돌아가기",
        backToHome: "홈으로 돌아가기",
        missingEmail: "이메일 주소를 입력하세요.",
        success:
            "이 이메일로 등록된 계정이 있으면 비밀번호 재설정 링크를 보냈습니다.",
        failed:
            "재설정 링크를 보낼 수 없습니다. 다시 시도해 주세요.",
    },

    fi: {
        title: "Palauta salasana",
        subtitle:
            "Syötä rekisteröity sähköpostiosoitteesi. Lähetämme sinulle palautuslinkin.",
        emailAddress: "Sähköpostiosoite",
        emailPlaceholder: "you@example.com",
        sendResetLink: "Lähetä palautuslinkki",
        sending: "Lähetetään...",
        backToLogin: "Takaisin kirjautumiseen",
        backToHome: "Takaisin etusivulle",
        missingEmail: "Syötä sähköpostiosoitteesi.",
        success:
            "Jos tällä sähköpostilla on olemassa tili, lähetimme salasanan palautuslinkin.",
        failed:
            "Palautuslinkkiä ei voitu lähettää. Yritä uudelleen.",
    },
};

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);

    const { language } = useLanguage();
    const text = forgotPasswordTexts[language] || forgotPasswordTexts.en;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim().toLowerCase();

        if (!cleanEmail) {
            setIsError(true);
            setMessage(text.missingEmail);
            return;
        }

        setLoading(true);
        setMessage("");
        setIsError(false);

        try {
            const response = await fetch("/api/auth/forgot-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ email: cleanEmail }),
            });

            if (!response.ok) {
                throw new Error("Forgot password request failed");
            }

            setIsError(false);
            setMessage(text.success);
        } catch (error) {
            console.error("Forgot password failed:", error);
            setIsError(true);
            setMessage(text.failed);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center gap-3 mb-8">
                    <Radio size={40} weight="duotone" className="text-cyan-400" />
                    <span
                        className="text-3xl font-black tracking-tighter text-white"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        WORLDofRADIO
                    </span>
                </div>

                <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8">
                    <h1
                        className="text-2xl sm:text-3xl tracking-tight font-bold mb-2 text-white"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        {text.title}
                    </h1>

                    <p className="text-sm text-gray-400 mb-8">
                        {text.subtitle}
                    </p>

                    {message && (
                        <div
                            className={`rounded-xl p-4 mb-6 text-sm border ${isError
                                    ? "bg-red-500/10 border-red-500/50 text-red-400"
                                    : "bg-cyan-400/10 border-cyan-400/40 text-cyan-300"
                                }`}
                            role={isError ? "alert" : "status"}
                        >
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label htmlFor="email" className="text-white mb-2 block">
                                {text.emailAddress}
                            </Label>

                            <div className="relative">
                                <At
                                    size={20}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                                />

                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (message) {
                                            setMessage("");
                                            setIsError(false);
                                        }
                                    }}
                                    className="pl-12 bg-[#0A0E17] border-white/10 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                                    placeholder={text.emailPlaceholder}
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 bg-cyan-400 hover:bg-cyan-300 text-black font-bold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? text.sending : text.sendResetLink}
                        </Button>
                    </form>

                    <div className="text-center mt-6">
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                        >
                            <ArrowLeft size={16} />
                            {text.backToLogin}
                        </Link>
                    </div>
                </div>

                <div className="text-center mt-6">
                    <Link
                        to="/"
                        className="text-sm text-gray-400 hover:text-cyan-400 transition-colors"
                    >
                        ← {text.backToHome}
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;